import axios from 'axios';
import { toast } from 'react-toastify';
import { setGlobalError } from '../features/errorSlice'; // Redux error slice
import API_CONFIG from '../config/apiConfig';
import { retryWithBackoff } from '../utils/apiRetry';
import 'react-toastify/dist/ReactToastify.css';

let store; // Placeholder for store injection

export const injectStore = (_store) => {
  store = _store;
};

let isRefreshing = false;
let refreshSubscribers = [];

// Notify all waiting requests with the new token
const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Create an axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  withCredentials: true, // keep session cookie
  timeout: API_CONFIG.timeout, // Increased for Render cold starts
  headers: {
    Accept: 'application/json',
  },
});

// Add a request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get the token from localStorage
    const token = localStorage.getItem('accessToken');

    // If token exists, add it to the headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Special handling for the submit-step endpoint
    if (config.url.includes('/doctors/onboarding/submit-step/')) {
      // For this endpoint, we need to use application/json
      config.headers['Content-Type'] = 'application/json';

      // No need to convert to FormData, just ensure the data is properly structured
      // The data is already in the correct format from the doctorApi.js file
    } else if (!(config.data instanceof FormData)) {
      // For all other non-FormData requests, set Content-Type to application/json
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh and global error management
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!store) return Promise.reject(error);
    const { auth } = store.getState();

    // Handle Token Expiration (401) - only for authenticated endpoints
    // Don't interfere with public API calls that might return 401
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check if this is a public endpoint that shouldn't trigger logout
      const publicEndpoints = [
        '/auth/login/', 
        '/auth/signup/', 
        '/auth/otp-', 
        '/auth/register/',
        '/auth/magic-link/',
        '/doctors/list/', 
        '/clinics/', 
        '/doctors/clinic/'
      ];
      const isPublicEndpoint = publicEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));
      
      if (isPublicEndpoint) {
        // For public endpoints, just reject without logging out or refreshing
        return Promise.reject(error);
      }

      // Only attempt token refresh if we have a refresh token and user was authenticated
      if (!auth.refreshToken || (!auth.user && !auth.accessToken)) {
        // No refresh token or not authenticated - don't logout, just reject
        // This might be a validation error, not an auth issue
        return Promise.reject(error);
      }

      // Attempt token refresh
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const { data } = await axios.post(
            `${axiosInstance.defaults.baseURL}/auth/token/refresh/`,
            { refresh: auth.refreshToken }
          );

          // Update the token in localStorage
          localStorage.setItem('accessToken', data.access);

          // Update the token in the Redux store
          store.dispatch({
            type: 'auth/updateTokens',
            payload: {
              accessToken: data.access,
              refreshToken: auth.refreshToken,
              user: auth.user,
            },
          });

          // Update the Authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${data.access}`;

          onRefreshed(data.access);
          isRefreshing = false;

          // Return the retried request - don't fall through to global error handler
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Only logout if refresh token is actually expired/invalid
          // Don't logout on network errors or other issues
          isRefreshing = false;
          const isTokenExpired = refreshError.response?.status === 401 || 
                                 refreshError.response?.data?.code === 'token_not_valid';
          
          if (isTokenExpired) {
            // Refresh token is expired - logout
            store.dispatch({ type: 'auth/logout' });
          }
          // Return the refresh error - don't fall through to global handler
          return Promise.reject(refreshError);
        }
      }

      // If already refreshing, wait for the refresh to complete
      return new Promise((resolve, reject) => {
        refreshSubscribers.push((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axiosInstance(originalRequest));
        });
        // Also handle refresh failure
        refreshSubscribers.push(() => {
          reject(error);
        });
      });
    }

    // 🔴 Global Error Handling
    // Skip global error handling if we already handled 401 in token refresh above
    if (error.response?.status === 401 && originalRequest._retry) {
      // Already handled by token refresh logic - just return the error
      return Promise.reject(error);
    }

    let message = 'An unexpected error occurred';
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          message = data?.detail || data?.error || 'Bad Request. Please check your input.';
          break;
        case 401:
          // Only show message - don't logout here (already handled above)
          // This might be a validation error, not an auth issue
          message = data?.detail || data?.error || 'Unauthorized access.';
          // DON'T logout here - token refresh handler already handles it
          break;
        case 403:
          message = data?.detail || data?.error || 'Forbidden. You do not have access.';
          // Don't logout on 403 - user might just not have permission for this resource
          break;
        case 404:
          message = data?.detail || data?.error || 'Resource not found.';
          // Don't logout on 404 - just a missing resource
          break;
        case 500:
          message = data?.detail || data?.error || 'Server error. Please try again later.';
          // Don't logout on 500 - server error, not auth issue
          break;
        default:
          message = data?.detail || data?.error || `Unexpected error: ${status}`;
      }
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      message = 'Request timeout. The server may be starting up. Please try again in a moment.';
      // Don't logout on timeout errors - likely Render cold start
    } else {
      message = 'Network error. Please check your internet connection.';
      // Don't logout on network errors
    }

    // ✅ Prevent raw HTML from showing up in Redux store
    if (typeof message !== 'string') {
      message = 'An error occurred. Please try again later.';
    }

    // Only show error toast for significant errors (not validation errors)
    // Validation errors (400) are usually shown in forms, so we don't need global toast
    // Only show toast for server errors (500), network errors, or auth errors (401 after refresh fails)
    const shouldShowToast = !error.response || 
                            (error.response.status >= 500) || 
                            (error.response.status === 401 && originalRequest._retry);
    
    if (shouldShowToast) {
      toast.error(message, { position: 'top-right' });
    }

    // Dispatch error to Redux for UI management (but silently for validation errors)
    if (error.response?.status !== 400) {
      store.dispatch(
        setGlobalError(error.response?.data?.detail || message)
      );
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
